// The SamsonArt lightbox, seen from the 3D page.
//
// The viewer is loaded as a script tag (public/index.html) rather than imported, because it
// lives outside this app — a CRA build cannot import a file from outside its own src/ — and it
// puts itself on the page as `window.SamsonViewer`. js/viewer.js says the same at its top, and
// names this page as the second of its two front-ends.
//
// The two decisions that are ours are kept here, away from three.js, in the way of the other
// Utils modules: what to hand the viewer, and how to notice it closing.

/**
 * What the viewer needs to show one artwork, or null if it cannot be shown.
 *
 * The record is already everything. `artSource.groupByCategory` resolves `url` against the
 * gallery root for every consumer and fills `title` from the API's title or filename, so
 * nothing here has to guess where the gallery lives or what the work is called — and the record
 * is on the mesh, stamped by AddImages.js, so a click never goes back to the API.
 *
 * @param {object} record - the artwork record carried on the mesh.
 * @returns {{src: string, title: string, mediaType: string}|null}
 */
export function lightboxArgsFor(record) {
    if (!record || !record.url) return null;

    return {
        src: record.url,
        title: record.title || '',
        mediaType: record.mediaType,
    };
}

/**
 * Calls `onClosed` once, when the viewer closes. Returns a function that stops watching.
 *
 * The viewer has no close hook — only `isOpen()` — so closing is an edge to poll for rather
 * than an event to subscribe to. Polling also covers every way it closes: the ✕, a click on
 * the backdrop, and Escape, all three wired inside js/viewer.js and not ours to hook.
 *
 * A viewer that is missing, or not open, gets a no-op stop function rather than an immediate
 * callback. That guard is not tidiness: a poll started against a viewer that never opened reads
 * it as closed on the first step and sends the artwork spinning home the instant it was clicked.
 *
 * @param {{isOpen: Function}} viewer - the singleton on `window.SamsonViewer`.
 * @param {Function} onClosed
 * @param {object} [options]
 * @returns {Function} stops the watch.
 */
export function watchForClose(viewer, onClosed, {
    interval = 100,
    setIntervalImpl = setInterval,
    clearIntervalImpl = clearInterval,
} = {}) {
    if (!viewer || !viewer.isOpen()) return () => {};

    const poll = setIntervalImpl(() => {
        if (viewer.isOpen()) return;

        clearIntervalImpl(poll);
        onClosed();
    }, interval);

    return () => clearIntervalImpl(poll);
}
